import { Directive, HostListener, Input, Renderer2 } from '@angular/core';
import { FormGroup, AbstractControl } from '@angular/forms';

@Directive({
  selector: '[baseScrollToInvalid]',
})
export class BaseScrollToInvalidDirective {
  @Input() baseInvalidForm: FormGroup | undefined;
  @Input() baseInvalidFormSubmitted = false;
  @Input() formContainer?: HTMLElement;

  constructor(private renderer: Renderer2) {}

  /**
   * Handles click events on the host element.
   * If the form was submitted and is invalid, scrolls to the first invalid control.
   * @returns {void}
   */
  @HostListener('click')
  onButtonClick(): void {
    if (!this.baseInvalidForm) {
      return;
    }

    if (!this.baseInvalidFormSubmitted || this.baseInvalidForm.valid) {
      return;
    }

    const invalidControl = this.findFirstInvalidControl(this.baseInvalidForm);
    if (invalidControl) {
      this.scrollToControl(invalidControl);
    }
  }

  /**
   * Recursively finds the first invalid control in the given form group or control.
   * @private
   * @param {AbstractControl} control - The control/form group to start the search from.
   * @returns {AbstractControl | null} - The first invalid control found, or null if none found.
   */
  private findFirstInvalidControl(control: AbstractControl): AbstractControl | null {
    if (control.invalid && !!control.errors) {
      return control;
    }

    if (control instanceof FormGroup) {
      for (const name in control.controls) {
        if (control.controls[name].invalid) {
          return this.findFirstInvalidControl(control.controls[name]);
        }
      }
    }

    return null;
  }

  /**
   * Scrolls to the specified invalid control in the DOM, highlights it, and focuses it for the user.
   * Determines if the field is inside a modal/tab container or should use its scrollable parent.
   * @private
   * @param {AbstractControl} control - The invalid form control to scroll to.
   * @returns {void}
   */
  private scrollToControl(control: AbstractControl): void {
    const name = this.getControlName(control);
    if (!name) {
      return;
    }

    let el = this.findControlElement(name);
    if (!el) {
      return;
    }

    const modalContent = document.querySelector('.overflow-modal-content') as HTMLElement;
    if (modalContent && modalContent.contains(el)) {
      this.smoothScrollContainerToElement(modalContent, el);
    } else {
      this.smoothScrollToScrollableParent(el);
    }

    el.classList.add('field-error-highlight');
    setTimeout(() => el.classList.remove('field-error-highlight'), 2000);
    setTimeout(() => el.focus(), 400);
  }

  /**
   * Finds the DOM element associated with the control name, searching inside formContainer if provided.
   * Prefers formGroupName for nested groups, then formControlName.
   * @private
   * @param {string} name - The name of the control to search for.
   * @returns {HTMLElement | null} - The matching DOM element or null if not found.
   */
  private findControlElement(name: string): HTMLElement | null {
    let el: HTMLElement | null;

    if (this.formContainer) {
      el =
        this.formContainer.querySelector(`[formGroupName="${name}"]`) ??
        this.formContainer.querySelector(`[formControlName="${name}"]`) ??
        this.formContainer.querySelector(`[controlName="${name}"]`);
    } else {
      el = document.querySelector(`[formGroupName="${name}"]`) ?? document.querySelector(`[formControlName="${name}"]`);
    }

    return el ?? null;
  }

  /**
   * Retrieves the name of the control within its parent form group.
   * @private
   * @param {AbstractControl} control - The control to retrieve the name for.
   * @returns {string | null} - The name of the control or null if not found.
   */
  private getControlName(control: AbstractControl): string | null {
    const parent = control.parent;
    if (!parent) {
      return null;
    }

    const formGroup = parent.controls;

    if (formGroup instanceof Array) {
      return null; // Add FormArray support if needed
    }
    const found = Object.keys(formGroup).find((name) => control === formGroup[name]);
    if (found) {
      return found;
    } else {
      return null;
    }
  }

  /**
   * Smoothly scrolls a scrollable container (e.g., modal tab content) to bring the element into view,
   * accounting for the container's offset and optional header/padding.
   * @private
   * @param {HTMLElement} container - The scrollable container element.
   * @param {HTMLElement} el - The element to bring into view.
   * @returns {void}
   */
  private smoothScrollContainerToElement(container: HTMLElement, el: HTMLElement): void {
    const containerRect = container.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const scrollTop = container.scrollTop;
    const offset = elRect.top - containerRect.top + scrollTop;

    container.scrollTo({ top: offset, behavior: 'smooth' });
  }

  /**
   * Smoothly scrolls the closest scrollable parent to bring the element into view.
   * Uses Renderer2 for Angular compatibility.
   * @private
   * @param {HTMLElement} el - The element to scroll into view.
   */
  private smoothScrollToScrollableParent(el: HTMLElement): void {
    if (el) {
      this.renderer.selectRootElement(el, true).scrollIntoView({ behavior: 'smooth' });
    }
  }
}
